import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  makeStyles,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  useMediaQuery,
  useTheme,
  TextField,
  Button
} from '@material-ui/core';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import {
  People as PeopleIcon,
  Payment as PaymentIcon,
  Assessment as AnalyticsIcon,
  SupervisorAccount as SuperAdminIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  PersonAdd as PersonAddIcon,
  CheckCircle as CheckCircleIcon,
  PlayArrow as PlayIcon
} from '@material-ui/icons';
import { useHistory } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { formatDistanceToNow } from 'date-fns';
import { GET_ANALYTICS_DATA } from '../../core/graphql/queries/analytics';
import artistSignUp, { artistSignUpResponse } from '../../core/graphql/queries/findArtistSignup';
import {
  SEND_MAILGUN_TEST_EMAIL,
  SendMailgunTestEmailResponse,
  SendMailgunTestEmailVariables,
} from '../../core/graphql/mutations/admin';
import { formatApolloErrorMessage } from '../../core/utils/formatApolloError';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: '#02172a',
    minHeight: '100vh',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  header: {
    marginBottom: theme.spacing(4),
  },
  headerTitle: {
    color: '#fff',
    fontWeight: 600,
    fontSize: '2.5rem',
    marginBottom: theme.spacing(1),
    letterSpacing: '-0.02em',
    [theme.breakpoints.down('sm')]: {
      fontSize: '2rem',
    },
  },
  headerSubtitle: {
    color: '#b0b0b0',
    fontSize: '1.1rem',
    lineHeight: 1.5,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem',
    },
  },
  statsGrid: {
    marginBottom: theme.spacing(4),
  },
  statCard: {
    backgroundColor: '#1a2332',
    border: '1px solid #2a3a4a',
    borderRadius: 12,
    transition: 'all 0.2s ease-in-out',
    height: '100%',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 8px 32px rgba(25, 118, 210, 0.15)',
      borderColor: '#1976d2',
    },
  },
  statCardContent: {
    padding: theme.spacing(3),
    '&:last-child': {
      paddingBottom: theme.spacing(3),
    },
  },
  statNumber: {
    color: '#fff',
    fontWeight: 700,
    fontSize: '2.25rem',
    lineHeight: 1,
    marginBottom: theme.spacing(0.5),
  },
  statLabel: {
    color: '#b0b0b0',
    fontSize: '0.9rem',
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  statIcon: {
    width: 48,
    height: 48,
    marginBottom: theme.spacing(2),
  },
  contentGrid: {
    marginBottom: theme.spacing(4),
  },
  section: {
    backgroundColor: '#1a2332',
    border: '1px solid #2a3a4a',
    borderRadius: 12,
    padding: theme.spacing(3),
    height: 'fit-content',
  },
  sectionTitle: {
    color: '#fff',
    fontWeight: 600,
    fontSize: '1.25rem',
    marginBottom: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
    '&::after': {
      content: '""',
      flex: 1,
      height: '1px',
      backgroundColor: '#2a3a4a',
      marginLeft: theme.spacing(2),
    },
  },
  actionsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: theme.spacing(2),
  },
  actionCard: {
    backgroundColor: '#1a2332',
    border: '1px solid #2a3a4a',
    borderRadius: 8,
    transition: 'all 0.2s ease-in-out',
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 20px rgba(25, 118, 210, 0.1)',
      borderColor: '#1976d2',
      backgroundColor: '#1e2a3a',
    },
  },
  actionCardContent: {
    padding: theme.spacing(2.5),
    '&:last-child': {
      paddingBottom: theme.spacing(2.5),
    },
  },
  actionIcon: {
    width: 40,
    height: 40,
    marginBottom: theme.spacing(1.5),
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionTitle: {
    color: '#fff',
    fontWeight: 600,
    fontSize: '1rem',
    marginBottom: theme.spacing(0.5),
  },
  actionDescription: {
    color: '#b0b0b0',
    fontSize: '0.875rem',
    lineHeight: 1.4,
  },
  activityList: {
    backgroundColor: 'transparent',
    padding: 0,
  },
  listItem: {
    padding: theme.spacing(1.5, 0),
    borderRadius: 6,
    marginBottom: theme.spacing(0.5),
    '&:hover': {
      backgroundColor: 'rgba(25, 118, 210, 0.08)',
    },
    '&:last-child': {
      marginBottom: 0,
    },
  },
  listItemText: {
    '& .MuiListItemText-primary': {
      color: '#fff',
      fontSize: '0.9rem',
      fontWeight: 500,
    },
    '& .MuiListItemText-secondary': {
      color: '#b0b0b0',
      fontSize: '0.8rem',
    },
  },
  activityAvatar: {
    width: 36,
    height: 36,
  },
  divider: {
    backgroundColor: '#2a3a4a',
    margin: theme.spacing(1, 0),
  },
  chip: {
    backgroundColor: '#1976d2',
    color: '#fff',
    fontSize: '0.75rem',
    height: 20,
    fontWeight: 600,
  },
  emailCard: {
    backgroundColor: '#1a2332',
    border: '1px solid #2a3a4a',
    borderRadius: 12,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  emailInput: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#02172a',
      color: '#fff',
      '& fieldset': {
        borderColor: '#2a3a4a',
      },
    },
    '& .MuiInputLabel-root': {
      color: '#b0b0b0',
    },
  },
  emailMeta: {
    color: '#b0b0b0',
    marginTop: theme.spacing(1),
    fontSize: '0.875rem',
  },
  warningItem: {
    color: '#ffb74d',
    marginTop: theme.spacing(0.5),
    fontSize: '0.875rem',
  },
}));

const AdminDashboard: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Date range state - default to "today"
  const [dateRange, setDateRange] = useState("today");

  const [testRecipient, setTestRecipient] = useState('');
  const [emailFeedback, setEmailFeedback] = useState<string | null>(null);
  const [emailWarnings, setEmailWarnings] = useState<string[]>([]);
  const [emailStatusSummary, setEmailStatusSummary] = useState<string | null>(null);

  const [sendMailgunTestEmail, { loading: emailTesting }] = useMutation<
    SendMailgunTestEmailResponse,
    SendMailgunTestEmailVariables
  >(SEND_MAILGUN_TEST_EMAIL);


  // Map dropdown selection to type parameter
  const queryType = useMemo(() => {
    switch (dateRange) {
      case 'today': return 'today';
      case 'past7days': return 'week';
      case 'pastMonth': return 'month';
      case 'thisYear': return 'year';
      case 'all': return 'allTime';
      default: return 'allTime';
    }
  }, [dateRange]);

  // Map dropdown values to type values for chart queries (artist signup)
  // These queries use "type" parameter - backend may only support: "week", "month", "year", "allTime"
  const getChartType = (range: string): string => {
    switch (range) {
      case 'today': return 'week'; // Use week for today since backend may not support 'today'
      case 'past7days': return 'week';
      case 'pastMonth': return 'month';
      case 'thisYear': return 'year';
      case 'all': return 'allTime';
      default: return 'allTime';
    }
  };

  // Fetch analytics data - now uses type parameter
  // Ensure queryType is always defined (default to 'today' if not set)
  const effectiveType = queryType || 'today';
  const { data: analyticsData, loading: analyticsLoading } = useQuery(GET_ANALYTICS_DATA, {
    variables: { type: effectiveType },
    fetchPolicy: 'network-only', // Force fresh data
  });

  // Log analytics data for debugging
  useEffect(() => {
    console.log('🔵 AdminDashboard - dateRange:', dateRange, '-> queryType:', queryType, '-> effectiveType:', effectiveType);
    console.log('🔵 AdminDashboard - Variables being sent:', { type: effectiveType });
    if (analyticsData) {
      console.log('🔵 AdminDashboard analyticsData:', analyticsData);
      console.log('🔵 AdminDashboard analytics values for', dateRange, ':', {
        totalPlays: analyticsData?.analyticsData?.totalPlays,
        totalEarnings: analyticsData?.analyticsData?.totalEarnings,
        totalArtists: analyticsData?.analyticsData?.totalArtists,
        type: effectiveType
      });
    }
  }, [analyticsData, dateRange, queryType, effectiveType]);

  // Fetch artist signup data for calculating new artists count
  const { data: artistSignupData, loading: artistSignupLoading } = useQuery<artistSignUpResponse>(artistSignUp, {
    variables: {
      type: getChartType(dateRange)
    },
    errorPolicy: 'all',
    skip: dateRange === 'all' // Skip if "All Time" - we'll use totalArtists instead
  });

  // Calculate new artists count for the selected period
  // For "All Time", use totalArtists from analytics
  // For other periods, sum up the artist signup data array
  const newArtistsCount = dateRange === 'all' 
    ? (analyticsData?.analyticsData?.totalArtists || 0)
    : (artistSignupData?.findAritst?.data && Array.isArray(artistSignupData.findAritst.data) && artistSignupData.findAritst.data.length > 0
        ? artistSignupData.findAritst.data.reduce((sum: number, count: number) => sum + (count || 0), 0)
        : 0);


  // Helper function to get icon and color for activity type
  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'ARTIST_REGISTERED':
        return { Icon: SuperAdminIcon, color: '#e91e63' };
      case 'AFFILIATE_APPLICATION':
        return { Icon: PersonAddIcon, color: '#2196f3' };
      case 'PAYOUT_PROCESSED':
        return { Icon: CheckCircleIcon, color: '#4caf50' };
      case 'PLAY':
        return { Icon: PlayIcon, color: '#ff9800' };
      default:
        return { Icon: AnalyticsIcon, color: '#9e9e9e' };
    }
  };

  // Format time to "X hours ago" format
  const formatTimeAgo = (timeString: string) => {
    try {
      const time = new Date(timeString);
      return formatDistanceToNow(time, { addSuffix: true });
    } catch (error) {
      return 'Unknown time';
    }
  };

  const adminFeatures = [
    {
      title: 'SuperAdmin',
      description: 'Manage and impersonate StarFarm artists',
      icon: <SuperAdminIcon />,
      path: '/admin/superadmin',
      color: '#e91e63',
      count: 0
    },
    {
      title: 'Affiliate Management',
      description: 'Manage affiliate accounts and applications',
      icon: <PeopleIcon />,
      path: '/admin/affiliates',
      color: '#2196f3',
      count: 0
    },
    {
      title: 'Payout Management',
      description: 'Process affiliate payouts and payments',
      icon: <PaymentIcon />,
      path: '/admin/payouts',
      color: '#4caf50',
      count: 0
    },
    {
      title: 'Analytics',
      description: 'View platform analytics and metrics',
      icon: <AnalyticsIcon />,
      path: '/admin/analytics',
      color: '#ff9800',
      count: 0
    },
    {
      title: 'Conversions',
      description: 'View all affiliate conversions and earnings',
      icon: <TrendingUpIcon />,
      path: '/admin/conversions',
      color: '#9c27b0',
      count: 0
    }
  ];

  const handleFeatureClick = (path: string) => {
    history.push(path);
  };

  const handleSendMailgunTest = async () => {
    if (!testRecipient.trim()) {
      setEmailFeedback('Enter a recipient email address.');
      setEmailWarnings([]);
      return;
    }

    try {
      const { data } = await sendMailgunTestEmail({
        variables: { to: testRecipient.trim() },
      });

      const result = data?.sendMailgunTestEmail;
      if (!result) {
        setEmailFeedback('No response received from the test email request.');
        setEmailWarnings([]);
        setEmailStatusSummary(null);
        return;
      }

      setEmailFeedback(result.message);
      setEmailWarnings(result.warnings || []);
      setEmailStatusSummary(
        `Domain: ${result.domain} | State: ${result.domainState} | Sender: ${result.fromEmail}`
      );
    } catch (error: unknown) {
      setEmailFeedback(formatApolloErrorMessage(error));
      setEmailWarnings([]);
      setEmailStatusSummary(null);
    }
  };

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
          <Box>
            <Typography variant="h3" className={classes.headerTitle}>
              Admin Dashboard
            </Typography>
            <Typography variant="body1" className={classes.headerSubtitle}>
              Welcome to the StarFarm Admin Panel. Here's what's happening on the platform.
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" style={{ marginTop: theme.spacing(2), marginLeft: theme.spacing(2) }}>
            <Typography variant="body2" style={{ color: '#b0b0b0', fontSize: '14px', marginRight: theme.spacing(1) }}>
              Display data for
            </Typography>
            <FormControl variant="standard" sx={{ m: 1, minWidth: 120 }} style={{ fontSize: '12px' }}>
              <Select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value as string)}
                style={{ color: '#fff' }}
              >
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="past7days">Last 7 Days</MenuItem>
                <MenuItem value="pastMonth">Last 30 Days</MenuItem>
                <MenuItem value="thisYear">This Year</MenuItem>
                <MenuItem value="all">All Time</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>

      <Paper className={classes.emailCard}>
        <Typography variant="h5" className={classes.sectionTitle}>
          Email Configuration Test
        </Typography>
        <Typography variant="body2" className={classes.actionDescription} style={{ marginBottom: 16 }}>
          Verify Mailgun domain and sender setup, then send a real test email to confirm delivery.
        </Typography>
        <Box display="flex" gridGap={12} flexDirection={isMobile ? 'column' : 'row'}>
          <TextField
            className={classes.emailInput}
            label="Test recipient email"
            variant="outlined"
            fullWidth
            value={testRecipient}
            onChange={(event) => setTestRecipient(event.target.value)}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleSendMailgunTest}
            disabled={emailTesting}
          >
            {emailTesting ? 'Testing...' : 'Send Test Email'}
          </Button>
        </Box>
        {emailFeedback && (
          <Typography className={classes.emailMeta}>{emailFeedback}</Typography>
        )}
        {emailStatusSummary && (
          <Typography className={classes.emailMeta}>{emailStatusSummary}</Typography>
        )}
        {emailWarnings.map((warning, index) => (
          <Typography key={index} className={classes.warningItem}>
            - {warning}
          </Typography>
        ))}
      </Paper>

      {/* Quick Stats Cards */}
      <Grid container spacing={3} className={classes.statsGrid}>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statCard}>
            <CardContent className={classes.statCardContent}>
              <Avatar 
                className={classes.statIcon} 
                style={{ backgroundColor: '#e91e63' }}
              >
                <SuperAdminIcon />
              </Avatar>
              <Typography variant="h3" className={classes.statNumber}>
                {(analyticsLoading || artistSignupLoading) ? '...' : newArtistsCount.toLocaleString()}
              </Typography>
              <Typography variant="body2" className={classes.statLabel}>
                Artists
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statCard}>
            <CardContent className={classes.statCardContent}>
              <Avatar 
                className={classes.statIcon} 
                style={{ backgroundColor: '#2196f3' }}
              >
                <PersonAddIcon />
              </Avatar>
              <Typography variant="h3" className={classes.statNumber}>
                {analyticsLoading ? '...' : '0'}
              </Typography>
              <Typography variant="body2" className={classes.statLabel}>
                Pending Affiliates
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statCard}>
            <CardContent className={classes.statCardContent}>
              <Avatar 
                className={classes.statIcon} 
                style={{ backgroundColor: '#4caf50' }}
              >
                <MoneyIcon />
              </Avatar>
              <Typography variant="h3" className={classes.statNumber}>
                {analyticsLoading ? '...' : `$${(analyticsData?.analyticsData?.totalEarnings || 0).toLocaleString()}`}
              </Typography>
              <Typography variant="body2" className={classes.statLabel}>
                Total Earnings
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statCard}>
            <CardContent className={classes.statCardContent}>
              <Avatar 
                className={classes.statIcon} 
                style={{ backgroundColor: '#ff9800' }}
              >
                <TrendingUpIcon />
              </Avatar>
              <Typography variant="h3" className={classes.statNumber}>
                {analyticsLoading ? '...' : (analyticsData?.analyticsData?.totalPlays || 0).toLocaleString()}
              </Typography>
              <Typography variant="body2" className={classes.statLabel}>
                Total Plays
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content */}
      <Grid container spacing={3} className={classes.contentGrid}>
        {/* Quick Actions */}
        <Grid item xs={12} lg={8}>
          <Paper className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              Quick Actions
            </Typography>
            <Box className={classes.actionsGrid}>
              {adminFeatures.map((feature, index) => (
                <Card 
                  key={index}
                  className={classes.actionCard}
                  onClick={() => handleFeatureClick(feature.path)}
                >
                  <CardContent className={classes.actionCardContent}>
                    <Box 
                      className={classes.actionIcon}
                      style={{ backgroundColor: `${feature.color}20` }}
                    >
                      <Box style={{ color: feature.color }}>
                        {feature.icon}
                      </Box>
                    </Box>
                    <Typography variant="h6" className={classes.actionTitle}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" className={classes.actionDescription}>
                      {feature.description}
                    </Typography>
                    {feature.count > 0 && (
                      <Chip 
                        label={feature.count} 
                        size="small" 
                        className={classes.chip}
                        style={{ marginTop: 8 }}
                      />
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12} lg={4}>
          <Paper className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              Recent Activity
            </Typography>
            <List className={classes.activityList}>
              {analyticsLoading ? (
                <ListItem className={classes.listItem}>
                  <ListItemText
                    primary="Loading activities..."
                    className={classes.listItemText}
                  />
                </ListItem>
              ) : analyticsData?.analyticsData?.recentActivity && analyticsData.analyticsData.recentActivity.length > 0 ? (
                analyticsData.analyticsData.recentActivity.map((activity: { type: string; title: string; description: string; time: string }, index: number) => {
                  const { Icon, color } = getActivityIcon(activity.type);
                  return (
                    <React.Fragment key={index}>
                      {index > 0 && <Divider className={classes.divider} />}
                      <ListItem className={classes.listItem}>
                        <ListItemAvatar>
                          <Avatar 
                            className={classes.activityAvatar} 
                            style={{ backgroundColor: color }}
                          >
                            <Icon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={activity.title}
                          secondary={`${activity.description || ''} • ${formatTimeAgo(activity.time)}`}
                          className={classes.listItemText}
                        />
                      </ListItem>
                    </React.Fragment>
                  );
                })
              ) : (
                <ListItem className={classes.listItem}>
                  <ListItemText
                    primary="No recent activity"
                    className={classes.listItemText}
                  />
                </ListItem>
              )}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminDashboard;